import React, { useEffect, useState } from 'react';
import { Button, Card, Tabs, List, Empty, Typography, Spin, message } from 'antd';
import { useHistory } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { useNotificationActions } from '_actions';
import { unreadNotificationsAtom } from '_state';

const { TabPane } = Tabs;
const { Text } = Typography;

export { NotificationFeed };

function fmt(ts) {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('vi-VN'); } catch (e) { return ''; }
}

function routeForNotification(n) {
    const t = n.target || {};
    if (t.type === 'attendance' && t.class_id) {
        return `/${t.class_id}/attendance${t.session_id ? `?sessionId=${encodeURIComponent(t.session_id)}` : ''}`;
    }
    if ((t.type === 'homework_submission' || t.type === 'homework_review') && t.class_id) {
        const q = new URLSearchParams();
        if (t.assignment_id) q.set('assignmentId', t.assignment_id);
        if (t.student_id) q.set('studentId', t.student_id);
        return `/${t.class_id}/home-assignments${q.toString() ? `?${q.toString()}` : ''}`;
    }
    if (t.type === 'tuition') {
        const q = new URLSearchParams();
        q.set('status', 'owed');
        if (t.tuition_student_id || t.student_id) q.set('studentId', t.tuition_student_id || t.student_id);
        return `/tuition?${q.toString()}`;
    }
    if (t.class_id) return `/${t.class_id}/dashboard`;
    const url = n.url || '';
    const ok = [
        /^\/[A-Za-z0-9._-]+\/attendance(\?.*)?$/,
        /^\/[A-Za-z0-9._-]+\/home-assignments(\?.*)?$/,
        /^\/tuition(\?.*)?$/,
        /^\/schedule(\?.*)?$/,
        /^\/manager\/students(\?.*)?$/,
        /^\/notifications$/,
    ].some(re => re.test(url));
    return ok ? url : null;
}

function FeedList({ items, onOpen }) {
    if (!items.length) return <Empty description="Chưa có thông báo" />;
    return (
        <List
            itemLayout="vertical"
            dataSource={items}
            renderItem={n => (
                <List.Item
                    className={n.read_at ? 'noti-row' : 'noti-row noti-row-unread'}
                    onClick={() => onOpen(n)}
                    style={{ cursor: 'pointer' }}
                >
                    <List.Item.Meta
                        title={<span>{!n.read_at && <span className="noti-dot" />} {n.title || '(Không tiêu đề)'}</span>}
                        description={<Text type="secondary">{fmt(n.createdAt)}</Text>}
                    />
                    {n.body}
                </List.Item>
            )}
        />
    );
}

function NotificationFeed({ flat = false }) {
    const actions = useNotificationActions();
    const history = useHistory();
    const [unread, setUnread] = useRecoilState(unreadNotificationsAtom);
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);

    const load = () => {
        actions.getMyNotifications().then(list => {
            setItems(list);
            setUnread((list || []).filter(n => !n.read_at).length);
            setLoading(false);
        });
    };

    useEffect(() => {
        load();
        /* eslint-disable-next-line */
    }, []);

    const openNotification = async (n) => {
        const route = routeForNotification(n);
        if (!n.read_at) {
            setItems(prev => prev.map(x => x._id === n._id ? { ...x, read_at: new Date().toISOString() } : x));
            setUnread(Math.max(0, unread - 1));
            actions.markRead(n._id).catch(() => load());
        }
        if (route) history.push(route);
        else message.info('Thông báo này không còn mở được trang đích');
    };

    const markAll = async () => {
        await actions.markAllRead();
        await load();
    };

    if (loading) return <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;

    // Staff feed (teachers/managers) is a single flat list — the chung/rieng split is student-only.
    if (flat) {
        return (
            <Card style={{ maxWidth: 720, margin: '0 auto' }}>
                <div style={{ textAlign: 'right', marginBottom: 8 }}>
                    <Button size="small" onClick={markAll}>Đánh dấu đã đọc</Button>
                </div>
                <FeedList items={items} onOpen={openNotification} />
            </Card>
        );
    }

    const chung = items.filter(n => n.section === 'chung');
    const rieng = items.filter(n => n.section !== 'chung');

    return (
        <Card style={{ maxWidth: 720, margin: '0 auto' }}>
            <div style={{ textAlign: 'right', marginBottom: 8 }}>
                <Button size="small" onClick={markAll}>Đánh dấu đã đọc</Button>
            </div>
            <Tabs defaultActiveKey="chung">
                <TabPane tab={`Thông báo chung (${chung.length})`} key="chung">
                    <FeedList items={chung} onOpen={openNotification} />
                </TabPane>
                <TabPane tab={`Thông báo riêng (${rieng.length})`} key="rieng">
                    <FeedList items={rieng} onOpen={openNotification} />
                </TabPane>
            </Tabs>
        </Card>
    );
}
